import { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Feature, useSimpleFeatureTernary } from "@viagogo/feature";
import type { IBannerItem } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { LocationContext } from "src/shared/context/location-context";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import {
  MarqueeExperimentFiltersPlacement,
  useMarqueeExperimentFiltersPlacement,
} from "../../hooks/useMarqueeExperimentFiltersPlacement";
import { useProgressiveCarouselLoadingV2 } from "../../hooks/useProgressiveCarouselLoadingV2";
import { ClearBannerContent } from "../clear-hero-banner/ClearBannerContent";
import { MarqueeHeroCarousel } from "../marquee-hero-carousel/MarqueeHeroCarousel";
import { HeroBannerSkeleton } from "./HeroBannerSkeleton";

interface Props {
  initialData: readonly IBannerItem[];
  selectedCarouselGenre: number;
}

export const HeroBanner = ({ initialData, selectedCarouselGenre }: Props) => {
  const createQueryFn = useCreateQueryFn();
  const filterPlacement = useMarqueeExperimentFiltersPlacement();
  const isProgressiveLoadingV2 = useProgressiveCarouselLoadingV2();

  const isLocationAwareFromExisting =
    useSimpleFeatureTernary(FeatureSetEnum.CXWD_HeroBannerResponsiveToLocation, false, true) ?? false;
  const isLocationAwareFromTrending =
    useSimpleFeatureTernary(FeatureSetEnum.CXWD_HeroBannerTrendingByUserLocation, false, true) ?? false;
  const isLocationAwareEnabled = isLocationAwareFromExisting || isLocationAwareFromTrending;

  const [contextLocation] = useContext(LocationContext);
  const locationLt = isLocationAwareEnabled ? (contextLocation?.lt ?? 0) : undefined;
  const locationLg = isLocationAwareEnabled ? (contextLocation?.lg ?? 0) : undefined;
  const genreFilterAppliesToHeroBanner = filterPlacement === MarqueeExperimentFiltersPlacement.Top;

  const queryKey = createQueryKey(QueryKeyPrefix.HERO_BANNER_CONTENT, {
    ...(genreFilterAppliesToHeroBanner && { topLevelCategoryId: selectedCarouselGenre }),
    ...(isLocationAwareEnabled && { locationLt, locationLg }),
  });
  const [initQueryKey] = useState(queryKey);

  const { data: heroBannerContent = initialData, isLoading } = useQuery({
    queryKey,
    queryFn: createQueryFn<IBannerItem[]>({
      method: "HeroBannerContent",
      params: isLocationAwareEnabled ? [["topLevelCategoryId", selectedCarouselGenre] as const] : [],
    }),
    // Only seed the cache when SSR actually provided banners: seeding an empty list would mark
    // the query as successful, so the enabled conditions below could never trigger the initial
    // fetch and the marquee would stay empty (the query does become stale after 60s and may
    // background-refetch, but that's harmless here — it only ever swaps in the same or newer
    // banner content).
    initialData: queryKey === initQueryKey && initialData.length > 0 ? initialData : undefined,
    // Also fetch on mount for V2 progressive variant users (TMHomeProgressiveCarouselLoadingV2 /
    // exp 103068): the server skips the hero banner fetch and the client loads it. Unlike the
    // other four lazy fetches, the hero has no null-vs-empty model signal (the SSR list is []
    // both for variant users and for pages with genuinely no banners), so this is gated on the
    // client experiment check, with the empty-list condition kept as a fallback in case the
    // client flag resolution ever disagrees with the server's variant decision. The fallback
    // costs at most one no-op refetch on control pages that truly have zero banners.
    enabled:
      genreFilterAppliesToHeroBanner || isLocationAwareEnabled || isProgressiveLoadingV2 || initialData.length === 0,
  });

  // Exp 103068 variant: the hero always occupies the top slot, so while the client-side
  // fetch is in flight hold its space with a skeleton — otherwise the banner pops in and
  // pushes the whole page down (the largest layout shift on home). Gated on the variant
  // check so control keeps its current behavior (including the rare zero-banner page,
  // where the fallback fetch fires but no skeleton should flash).
  if (isProgressiveLoadingV2 && isLoading && heroBannerContent.length === 0) {
    return <HeroBannerSkeleton />;
  }

  return (
    <Feature
      id={FeatureSetEnum.CXWD_3350_DesktopMarqueeCardsHeroBanner}
      control={() => (
        <ClearBannerContent
          categories={heroBannerContent.map((cat, idx) => ({
            ...cat,
            idx,
          }))}
          showEyebrow={false}
          narrowTopMargin
        />
      )}
      variant={() => <MarqueeHeroCarousel items={heroBannerContent} />}
    />
  );
};
